import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { CalendarClock } from 'lucide-react';
import type { MonthlyTicketItem } from '@sime/shared';
import { STATE_COLORS, STATE_LABELS } from '@sime/shared';
import { DocumentosBadge } from '@/components/ui/documentos-badge';
import { RescheduleDialog } from './reschedule-dialog';
import { cn } from '@/lib/utils';

const HOVER_DELAY_MS = 300;
const TOOLTIP_WIDTH = 280;
const TOOLTIP_MIN_HEIGHT = 120; // safe approximation for viewport-edge logic
const VIEWPORT_MARGIN = 8;

const MONTHS = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
];

function formatTicketDate(dateStr: string): string {
  const parts = dateStr.split('-');
  const y = parts[0] ?? '';
  const m = Number(parts[1]);
  const d = parts[2] ?? '';
  if (!m || m < 1 || m > 12) return dateStr;
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

const STATUS_BORDER: Record<string, string> = {
  PROGRAMADO: 'border-l-blue-500',
  INICIADO: 'border-l-orange-400',
  EJECUTADO: 'border-l-green-500',
  APROBADO: 'border-l-cyan-500',
  RECHAZADO: 'border-l-red-500',
  NO_EJECUTADO: 'border-l-neutral-400',
  REPROGRAMADO: 'border-l-purple-500',
};

const STATUS_DOT: Record<string, string> = {
  PROGRAMADO: 'bg-blue-500',
  INICIADO: 'bg-orange-400',
  EJECUTADO: 'bg-green-500',
  APROBADO: 'bg-cyan-500',
  RECHAZADO: 'bg-red-500',
  NO_EJECUTADO: 'bg-neutral-400',
  REPROGRAMADO: 'bg-purple-500',
};

const DRAGGABLE_STATES = new Set(['PROGRAMADO', 'INICIADO']);

interface TicketCardProps {
  ticket: MonthlyTicketItem;
  isRescheduling?: boolean;
  onDragStart: (ticketId: number, estado: string) => void;
}

export function TicketCard({ ticket, isRescheduling = false, onDragStart }: TicketCardProps) {
  const navigate = useNavigate();
  const isDraggable = DRAGGABLE_STATES.has(ticket.estadoOt) && !isRescheduling;
  const isReschedulable = DRAGGABLE_STATES.has(ticket.estadoOt);
  const isPlanned = ticket.planMatriz !== null;
  // NPL tickets have no planMatriz — their activity name lives in `servicio`.
  // Fall back to tipoOt only when both are absent (never show the bare "NPL").
  const label = ticket.planMatriz?.nombre ?? ticket.servicio ?? ticket.tipoOt?.nombre ?? '';
  const borderColor = STATUS_BORDER[ticket.estadoOt] ?? 'border-l-neutral-300';
  const dotColor = STATUS_DOT[ticket.estadoOt] ?? 'bg-neutral-300';
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- defensive: untrusted estadoOt from API may not match EstadoOt union
  const statusBg = STATE_COLORS[ticket.estadoOt]?.bg ?? '#8E8E93';
  const [rescheduleOpen, setRescheduleOpen] = useState(false);

  // Hover preview — shows after a delay, escapes overflow-hidden via portal.
  const cardRef = useRef<HTMLDivElement>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [previewPos, setPreviewPos] = useState<{ top: number; left: number } | null>(null);

  const showPreview = useCallback(() => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => {
      const rect = cardRef.current?.getBoundingClientRect();
      if (!rect) return;
      // Prefer the right side of the card; flip to left if overflowing the viewport.
      let left = rect.right + VIEWPORT_MARGIN;
      if (left + TOOLTIP_WIDTH + VIEWPORT_MARGIN > window.innerWidth) {
        left = Math.max(VIEWPORT_MARGIN, rect.left - TOOLTIP_WIDTH - VIEWPORT_MARGIN);
      }
      // Clamp vertically.
      let top = rect.top;
      if (top + TOOLTIP_MIN_HEIGHT + VIEWPORT_MARGIN > window.innerHeight) {
        top = Math.max(VIEWPORT_MARGIN, window.innerHeight - TOOLTIP_MIN_HEIGHT - VIEWPORT_MARGIN);
      }
      setPreviewPos({ top, left });
    }, HOVER_DELAY_MS);
  }, []);

  const hidePreview = useCallback(() => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
    setPreviewPos(null);
  }, []);

  // Cleanup pending timer on unmount to avoid setState-after-unmount.
  useEffect(() => () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
  }, []);

  // Preserve the calendar context (tab + period + filters) so the back-button
  // on TicketDetailPage returns to the same view the user came from.
  const navigateOptions = {
    state: { from: window.location.pathname + window.location.search },
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    hidePreview();
    void navigate(`/tickets/${ticket.id}`, navigateOptions);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      void navigate(`/tickets/${ticket.id}`, navigateOptions);
    }
  };

  const handleDragStart = (e: React.DragEvent) => {
    if (!isDraggable) return;
    hidePreview();
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(ticket.id));
    onDragStart(ticket.id, ticket.estadoOt);
  };

  return (
    <div
      ref={cardRef}
      role="button"
      tabIndex={0}
      aria-label={`Ticket ${ticket.correlativo}: ${label}. Estado: ${STATE_LABELS[ticket.estadoOt]}${isDraggable ? '. Arrastrable.' : ''}`}
      draggable={isDraggable}
      onDragStart={isDraggable ? handleDragStart : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={showPreview}
      onMouseLeave={hidePreview}
      onFocus={showPreview}
      onBlur={hidePreview}
      className={cn(
        'group relative flex items-start gap-1.5 rounded-lg border-l-[3px] bg-sime-surface',
        'px-1.5 py-1 text-xs shadow-neu-xxs transition-all duration-100',
        'hover:shadow-neu-xs hover:translate-x-px',
        'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-sime-accent',
        borderColor,
        isDraggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
      )}
      style={{ backgroundImage: `linear-gradient(${statusBg}1A, ${statusBg}1A)` }}
    >
      {/* Status dot */}
      <span className={cn('mt-[3px] h-1.5 w-1.5 shrink-0 rounded-full', dotColor)} aria-hidden="true" />

      {/* Two-line content */}
      <div className="min-w-0 flex-1">
        {/* Line 1: correlativo */}
        <p className="truncate text-[11px] font-semibold leading-tight text-sime-accent">
          {ticket.correlativo}
        </p>
        {/* Line 2: description */}
        <p className="truncate text-[10px] leading-tight text-sime-text-secondary">
          {label}
        </p>
      </div>

      {/* Badges column — PL/NPL, priority, optional PDF link */}
      <div className="flex shrink-0 flex-col items-end gap-0.5">
        <span
          className={cn(
            'rounded px-1 py-0.5 text-[10px] font-semibold leading-none',
            isPlanned
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
              : 'bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400',
          )}
        >
          {isPlanned ? 'PL' : 'NPL'}
        </span>

        {ticket.prioridad ? (
          <span
            className="rounded bg-amber-100 px-1 py-0.5 text-[10px] font-semibold leading-none text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
            aria-label="Alta prioridad"
          >
            !
          </span>
        ) : null}

        {/* Icon-only: the grid cell has ~20px of horizontal room per card. The
            hover preview below spells out what the icons mean. */}
        <DocumentosBadge
          ticketId={ticket.id}
          correlativo={ticket.correlativo}
          documentos={ticket.documentos}
          size="xs"
        />

        {/* Quick reschedule — appears on card hover/focus to keep the cell uncluttered. */}
        {isReschedulable ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              hidePreview();
              setRescheduleOpen(true);
            }}
            onKeyDown={(e) => { e.stopPropagation(); }}
            className={cn(
              'p-1 rounded-md text-sime-accent hover:bg-sime-accent/10 transition-all',
              'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
              'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-sime-accent',
            )}
            aria-label={`Reprogramar ${ticket.correlativo}`}
            title="Reprogramar a otra fecha"
          >
            <CalendarClock className="h-3 w-3" />
          </button>
        ) : null}
      </div>

      {previewPos
        ? createPortal(
            <div
              role="tooltip"
              className={cn(
                'pointer-events-none fixed rounded-xl bg-white p-3 shadow-lg border border-sime-border-subtle',
                'dark:bg-neutral-900 dark:border-neutral-700',
              )}
              style={{
                top: previewPos.top,
                left: previewPos.left,
                width: TOOLTIP_WIDTH,
                zIndex: 350,
                borderLeftWidth: 4,
                borderLeftColor: statusBg,
              }}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-bold text-sime-accent">{ticket.correlativo}</span>
                <span
                  className="rounded-full px-2 py-0.5 text-[10px] font-semibold leading-none"
                  style={{
                    backgroundColor: statusBg,
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- defensive: untrusted estadoOt from API may not match EstadoOt union
                    color: STATE_COLORS[ticket.estadoOt]?.text ?? '#fff',
                  }}
                >
                  {STATE_LABELS[ticket.estadoOt]}
                </span>
              </div>

              {label ? (
                <p className="text-xs text-sime-text-primary font-medium leading-tight mb-1">
                  {label}
                </p>
              ) : null}

              <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-[11px] mt-1">
                {ticket.ubicacion?.nombre ? (
                  <>
                    <dt className="text-sime-text-tertiary">Ubicación</dt>
                    <dd className="text-sime-text-secondary truncate">{ticket.ubicacion.nombre}</dd>
                  </>
                ) : null}
                <dt className="text-sime-text-tertiary">Fecha</dt>
                <dd className="text-sime-text-secondary">{formatTicketDate(ticket.fecha)}</dd>
                <dt className="text-sime-text-tertiary">Tipo</dt>
                <dd className="text-sime-text-secondary">{isPlanned ? 'Planeado' : 'No planeado'}</dd>
                {/* The grid card only has room for icons; the hover preview is
                    where they get spelled out. Certificados (the certification
                    documents) and the ticket's own PDF are listed separately —
                    conflating them is what made "no veo los certificados"
                    ambiguous in the first place. */}
                <dt className="text-sime-text-tertiary">Certificados</dt>
                <dd
                  className={cn(
                    ticket.documentos.certificadoCount > 0
                      ? 'font-semibold text-blue-700 dark:text-blue-300'
                      : 'text-sime-text-secondary',
                  )}
                >
                  {ticket.documentos.certificadoCount > 0
                    ? `${String(ticket.documentos.certificadoCount)} adjunto(s)`
                    : 'Sin certificados'}
                </dd>
                <dt className="text-sime-text-tertiary">PDF ticket</dt>
                <dd className="text-sime-text-secondary">
                  {ticket.documentos.pdfEstado === 'FIRMADO'
                    ? 'Firmado'
                    : ticket.documentos.pdfEstado === 'SIN_FIRMAR'
                      ? 'Sin firmar'
                      : 'No generado'}
                </dd>
              </dl>

              {ticket.prioridad ? (
                <p className="mt-1.5 inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                  Alta prioridad
                </p>
              ) : null}
            </div>,
            document.body,
          )
        : null}

      {/* Mount the dialog only when actually opened so its query hooks don't
          run for every card on screen (and so card tests don't need a
          QueryClientProvider). */}
      {rescheduleOpen ? (
        <RescheduleDialog
          open
          onClose={() => { setRescheduleOpen(false); }}
          ticketId={ticket.id}
          correlativo={ticket.correlativo}
          currentDate={ticket.fecha}
        />
      ) : null}
    </div>
  );
}
